import { createPinia } from 'pinia';
import { ViteSSG } from 'vite-ssg';
import type { Component } from 'vue';

import auth0 from './auth/auth0Plugin';
import { DEFAULT_LANGUAGE, initI18n, LocaleFileSuffix } from './i18n-setup';
import { installGuards, routes } from './router';
import spaScrollBehaviourHandler from './router/spaScrollBehaviourHandler';

// Import Domus
import domus, { HgSupportedLanguage, SupportedPlatforms } from '@smg-real-estate/domus';

// Load the base Domus styles
import { useRumContextTracking } from '@/composables/tracking/useRumContextTracking';
import { sectionImpressionDirective } from '@/directives/sectionImpressionDirective';
import { initBrowserRum } from '@/services/datadog/DatadogService';
import '@smg-real-estate/domus/style-utility-grid.scss';

export const createMyApp = (App: Component, platform: SupportedPlatforms = SupportedPlatforms.hg) =>
  ViteSSG(
    App,
    { routes, base: import.meta.env.BASE_URL, scrollBehavior: spaScrollBehaviourHandler },
    async (ctx) => {
      const { app, router, isClient, routePath } = ctx;

      const locale = (router.currentRoute.value.params.locale ??
        routePath?.split('/')[1]) as HgSupportedLanguage;
      const i18n = await initI18n(
        platform === SupportedPlatforms.hg ? LocaleFileSuffix.HG : LocaleFileSuffix.IS24,
        locale,
      );

      app.use(createPinia());
      app.directive('impression', sectionImpressionDirective);
      app.use(i18n);
      initBrowserRum();
      useRumContextTracking(router);
      app.use(auth0);

      installGuards(ctx);

      // Install Domus
      app.use(domus, {
        theme: platform,
        i18n: { instance: i18n, languages: { name: 'all', mode: 'lazy' } },
        router: { instance: router, defaultLanguage: DEFAULT_LANGUAGE },
      });

      // This allows accessing the Vue application from within Cypress tests.
      // Source: https://www.cypress.io/blog/2017/11/28/testing-vue-web-application-with-vuex-data-store-and-rest-backend/
      if (isClient && (window as any).Cypress) {
        // only available during E2E tests
        (window as any).app = app;
      }
    },
  );
